import type {
  StatsigClientForAnalyticsLogger,
  StatsigServerClientForAnalyticsLogger,
} from "./analytics-logger";

type AnalyticsStatsigContext = ReturnType<StatsigClientForAnalyticsLogger["getContext"]>;

type AnalyticsStatsigStructuredEventOptions = {
  user?: Parameters<StatsigServerClientForAnalyticsLogger["logEvent"]>[0];
};

export type AnalyticsStatsigClient = {
  readonly sdkType: "client" | "server";
  canLogEvent: () => boolean;
  flush: () => unknown;
  getContext: () => AnalyticsStatsigContext | undefined;
  getEnvironmentTier: () => unknown;
  getLogEventUrl: () => string | null | undefined;
  logStructuredEvent: (
    eventName: string,
    envelope: unknown,
    options?: AnalyticsStatsigStructuredEventOptions,
  ) => void;
  validateStructuredEventOptions: (options?: AnalyticsStatsigStructuredEventOptions) => void;
};

export function createAnalyticsStatsigClient({
  statsigClient,
  statsigServerClient,
}: {
  statsigClient?: StatsigClientForAnalyticsLogger;
  statsigServerClient?: StatsigServerClientForAnalyticsLogger;
}): AnalyticsStatsigClient | undefined {
  if (statsigClient !== undefined) {
    return {
      sdkType: "client",
      canLogEvent: () => typeof statsigClient.logEvent === "function",
      flush: () => (typeof statsigClient.flush === "function" ? statsigClient.flush() : undefined),
      getContext: () => statsigClient.getContext(),
      getEnvironmentTier: () => statsigClient.getContext().options?.environment?.tier,
      getLogEventUrl: () => statsigClient.networkConfig?.logEventUrl,
      logStructuredEvent: (eventName, envelope) => {
        if (typeof statsigClient.logEvent !== "function") {
          return;
        }
        statsigClient.logEvent({
          eventName,
          // StatsigEvent metadata is typed as Record<string, string> in
          // @oai-statsig/js-client, but CES expects a structured envelope object.
          // @ts-expect-error metadata needs to carry the protobuf envelope object.
          metadata: envelope,
        });
      },
      validateStructuredEventOptions: () => {},
    };
  }

  if (statsigServerClient !== undefined) {
    return {
      sdkType: "server",
      canLogEvent: () => true,
      flush: () => statsigServerClient.flushEvents(),
      getContext: () => undefined,
      getEnvironmentTier: () => undefined,
      getLogEventUrl: () => statsigServerClient.options?.logEventUrl,
      logStructuredEvent: (eventName, envelope, options) => {
        statsigServerClient.logEvent(
          options!.user!,
          eventName,
          null,
          // The server SDK metadata type is string-only, but CES expects the
          // structured protobuf envelope object.
          envelope as unknown as Record<string, string>,
        );
      },
      validateStructuredEventOptions: (options) => {
        if (options?.user === undefined) {
          throw new Error(
            "AnalyticsLogger.trackStructuredEvent requires user when using server Statsig logEvent transport.",
          );
        }
      },
    };
  }

  return undefined;
}
